import React from 'react';

import type { RawEditAnalyticsEvent } from '@gitbook/app-analytics';
import { type Block, BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import type { List } from '@gitbook/util-immutable';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { DragToSelect, isPrimaryButtonPressed, rectanglesIntersect } from '../../../Components';
import type { ScrollRef } from './PageSheetView';

/**
 * Use drag rectangle to select multiple blocks in the content editor.
 */
export const DragSelection = React.memo(function DragSelection(props: {
    disabled: boolean;
    editorRef: React.RefObject<EditorContainer>;
    scrollRef: ScrollRef;
    selectableAreaRef?: React.RefObject<HTMLDivElement>;
    logAnalyticsEvent: (event: RawEditAnalyticsEvent) => void;
}) {
    const { disabled = false, editorRef, scrollRef, selectableAreaRef } = props;

    const selectionFilter = useEventCallback((event: PointerEvent) => {
        const editor = editorRef.current;

        // if the event is not a single left click, we don't want to start a drag selection
        if (!isPrimaryButtonPressed(event)) {
            return false;
        }

        // Magic Mouse / trackpad scroll gestures report pointer events without pressure even when no button is pressed.
        // Ignore those so inertial scrolling can't seed a drag selection.
        if (event.pointerType !== 'mouse' && event.pressure === 0) {
            return false;
        }

        // if the editor is composing, we don't want to start a drag selection
        if (!editor || editor.isComposing) {
            return false;
        }

        if (!event.target || !(event.target instanceof HTMLElement)) {
            return false;
        }

        const targetElement = event.target;

        // ignore elements outside the selectable area (i.e ignore sidebar, aside, toc, etc.)
        if (!selectableAreaRef?.current?.contains(targetElement)) {
            return false;
        }

        // ignore any content within blocks
        if (targetElement.closest('[data-block-content]')) {
            return false;
        }

        // ignore any interactive elements
        if (
            targetElement.matches(
                '[tabindex], a[href], button:enabled, input:enabled, textarea:enabled, select:enabled, option:enabled'
            )
        ) {
            return false;
        }

        // ignore any element that has the data-drag-selection-ignore attribute or is a child of such an element
        if (
            Array.from(document.querySelectorAll('[data-drag-selection-ignore]')).some((el) => {
                return el === targetElement || el.contains(targetElement);
            })
        ) {
            return false;
        }

        return true;
    });

    /**
     * Debounced method with updated selection rectangle.
     */
    const onSelectionChange = useEventCallback((rectangle: DOMRect) => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        const editorDomRef = editor.elementRef?.current;
        if (!editorDomRef) {
            return;
        }

        const intersectingBlocks: List<Block> = editor.value.document
            .getBlocks()
            .filter((block) => {
                const blockInDOM =
                    editorDomRef.querySelector(
                        `[data-block-content="${block.key}"]` // more accurate hit area from block content.
                    ) ?? editorDomRef.querySelector(`[data-key="${block.key}"]`); // but having to use data-key for virtualized blocks

                if (blockInDOM) {
                    const blockRect = blockInDOM.getBoundingClientRect();
                    return rectanglesIntersect(blockRect, rectangle);
                }
                return false;
            });

        if (!intersectingBlocks || intersectingBlocks.isEmpty()) {
            if (!isBlockRangeSelection(editor.value.selection) || editor.value.isSelectionUnset) {
                return; // skip the change if the selection is unset
            }
            editor.change((change) => {
                change.switchSelection().deselect(); // remove selection from the editor
            });
        } else {
            const first = intersectingBlocks.first();
            const last = intersectingBlocks.last();
            if (!first || !last) {
                return; // skip the change if there are no blocks
            }
            if (
                isBlockRangeSelection(editor.value.selection) &&
                editor.value.selection.anchorBlock === first.key &&
                editor.value.selection.focusBlock === last.key
            ) {
                return; // skip the change if the selection is the same
            }
            editor.change((change) => {
                return isBlockRangeSelection(change.value.selection)
                    ? change.select(
                          change.value.selection.moveAnchorTo(first.key).moveFocusTo(last.key)
                      )
                    : change.select(
                          BlockRange.create({
                              anchorBlock: intersectingBlocks.first()?.key,
                              focusBlock: intersectingBlocks.last()?.key,
                          })
                      );
            });
        }
    });

    const onSelectionEnd = useEventCallback(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        if (isBlockRangeSelection(editor.value.selection)) {
            if (editor.value.selection.isBlurred) {
                // wait until the next frame to focus the editor
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        editor.change((change) => change.focus());
                    }, 0);
                });
            }
            props.logAnalyticsEvent({
                type: 'edit_drag_selection',
                size: editor.value.blocks.size,
            });
        }
    });

    // remove selection from the editor
    const onUnselect = useEventCallback(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        if (isBlockRangeSelection(editor.value.selection)) {
            editor.change((change) => {
                change.switchSelection().deselect();
            });
        }
    });

    return (
        <DragToSelect
            scrollRef={scrollRef}
            disabled={disabled}
            selectionFilter={selectionFilter}
            onSelectionChange={onSelectionChange}
            onSelectionEnd={onSelectionEnd}
            onUnselect={onUnselect}
        />
    );
});
